import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getAttachmentTypes,
	getAttachmentTypesLoading,
	getEmAttachmentUploadLoading,
} from 'redux/reducers/attachments/selectors';
import {
	getAttachmentTypesRequest,
	uploadEmAttachmentRequest,
} from 'redux/reducers/attachments/reducer';

type TProps = {
	open: boolean;
	setClose: () => void;
	type?: string;
};

export const AddAttachmentDialog = ({ open, setClose, type }: TProps) => {
	const dispatch = useDispatch();
	const { emId } = useParams();
	const types = useSelector(getAttachmentTypes);
	const loadingTypes = useSelector(getAttachmentTypesLoading);
	const uploading = useSelector(getEmAttachmentUploadLoading);

	const [typeId, setTypeId] = useState<number | ''>('');
	const [comment, setComment] = useState<string>('');
	const [file, setFile] = useState<File | null>(null);

	useEffect(() => {
		if (type) {
			setTypeId(types?.find(t => t.type === type)?.id ?? '');
		}
	}, [type, types]);

	useEffect(() => {
		if (open) {
			dispatch(getAttachmentTypesRequest());
		}
	}, [dispatch, open]);

	const isSubmitDisabled = useMemo(
		() => !emId || !file || !typeId || uploading,
		[emId, file, typeId, uploading],
	);

	const handleSubmit = () => {
		if (!emId || !file || !typeId) return;
		dispatch(
			uploadEmAttachmentRequest({
				emId: Number(emId),
				file,
				typeId: Number(typeId),
				comment: comment?.trim() ? comment.trim() : null,
				listRequest: { emId: Number(emId), pageNumber: 1, pageSize: PAGE_SIZE },
			}),
		);
		setClose();
		setTypeId('');
		setComment('');
		setFile(null);
	};

	return (
		<Dialog open={open} onClose={setClose} fullWidth maxWidth="sm">
			<DialogTitle>Add Attachment</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
					<TextField
						select
						label="Type"
						value={typeId}
						onChange={e => setTypeId(e.target.value === '' ? '' : Number(e.target.value))}
						disabled={loadingTypes || uploading || !!type}
						required
						size="small"
					>
						<MenuItem value="" disabled>
							Select type
						</MenuItem>
						{types
							?.filter(t => !t.isHidden)
							.map(t => (
								<MenuItem key={t.id} value={t.id}>
									{t.type}
								</MenuItem>
							)) ?? null}
					</TextField>
					<TextField
						label="Comment (optional)"
						value={comment}
						onChange={e => setComment(e.target.value)}
						disabled={uploading}
						size="small"
						multiline
						minRows={2}
						maxRows={6}
					/>
					<Button variant="outlined" component="label" disabled={uploading}>
						{file ? file.name : 'Choose file'}
						<input
							type="file"
							hidden
							onChange={e => {
								const f = e.target.files?.[0] ?? null;
								setFile(f);
							}}
						/>
					</Button>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={setClose} disabled={uploading}>
					Cancel
				</Button>
				<Button onClick={handleSubmit} disabled={isSubmitDisabled} variant="contained">
					{uploading ? <CircularProgress size={18} /> : 'Upload'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
